import styled, { css } from 'styled-components';

import { size } from '../../layout/helpers';
import { inputPlaceholder } from '../../layout/reset';
import { Button } from '../Button';

export const Wrapper = styled.div.attrs(() => ({
  role: 'search',
}))`
  position: relative;
`;

export const Label = styled.label`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

interface SearchProps {
  $fullWidth?: boolean;
  $collapsible?: boolean;
}

export const Search = styled.div<SearchProps>`
  flex: ${({ $fullWidth }) => ($fullWidth ? '1 1 auto' : '0 1 auto')};
  position: relative;
  display: flex;
  align-items: center;
  background-color: var(--c-accent-bg);
  border-radius: 100vmax;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : '100px')};
  margin-left: ${({ $fullWidth }) => ($fullWidth ? '0' : 'auto')};

  &:focus-within,
  &:has(input:not(:placeholder-shown)) {
    flex: 1 1 auto;
    width: 100%;
    margin-left: 0;
  }

  &:focus-within {
    outline: 2px solid var(--c-focus);
  }

  ${Button} {
    margin-right: ${size(0.5)};
  }

  ${({ $collapsible }) =>
    $collapsible &&
    css`
      flex: 0 1 auto;
      width: 100px;
      margin-left: auto;

      &:focus-within,
      &:has(input:not(:placeholder-shown)) {
        flex: 1 1 auto;
        width: 100%;
        margin-left: 0;
      }
    `}
`;

interface InputProps {
  $small?: boolean;
  $sizeIconButton?: boolean;
}

const inputPlaceholderSmallStyle = css`
  transform: scale(0.875);
  transform-origin: 0 50%;
`;

export const Input = styled.input.attrs(() => ({
  type: 'search',
}))<InputProps>`
  ${inputPlaceholder};
  flex: 1 1 auto;
  display: block;
  width: 100%;
  height: var(--_input-height, ${(props) => props.theme.distances.inputHeight});
  padding: 0 ${size(2)} 0 ${size(1)};
  background-color: transparent;
  font-size: 16px;
  border: none;
  border-radius: inherit;
  box-shadow: none;
  appearance: none;

  &:focus,
  &:focus-visible {
    outline: none;
  }

  ${({ $small }) =>
    $small &&
    css`
      --_input-height: ${size(5)};

      &::-webkit-input-placeholder {
        ${inputPlaceholderSmallStyle}
      }

      &:-moz-placeholder {
        ${inputPlaceholderSmallStyle}
      }

      &::-moz-placeholder {
        ${inputPlaceholderSmallStyle}
      }

      &:-ms-input-placeholder {
        ${inputPlaceholderSmallStyle}
      }
    `}

    ${({ $sizeIconButton }) =>
      $sizeIconButton &&
      css`
      --_input-height: var(--icon-button-size);

      &::-webkit-input-placeholder {
        ${inputPlaceholderSmallStyle}
      }

      &:-moz-placeholder {
        ${inputPlaceholderSmallStyle}
      }

      &::-moz-placeholder {
        ${inputPlaceholderSmallStyle}
      }

      &:-ms-input-placeholder {
        ${inputPlaceholderSmallStyle}
      }
    `}
`;

export const Icon = styled.label`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding-left: ${size(2)};
  color: var(--c-text-muted);
  cursor: text;
`;
